export interface DialCodeEntry {
  dialCode: string;
  flag: string;
  name: string;
}

/**
 * ISO 3166-1 alpha-3 → dial code + flag mapping.
 * Flags are Unicode regional indicator pairs derived from the ISO2 code.
 * Country names here are fallback only — display names come from the GraphQL API.
 */
export const countryDialCodes: Record<string, DialCodeEntry> = {
  // Priority countries
  HRV: { dialCode: "+385", flag: "🇭🇷", name: "Croatia" },
  SVN: { dialCode: "+386", flag: "🇸🇮", name: "Slovenia" },
  DEU: { dialCode: "+49", flag: "🇩🇪", name: "Germany" },
  AUT: { dialCode: "+43", flag: "🇦🇹", name: "Austria" },
  ITA: { dialCode: "+39", flag: "🇮🇹", name: "Italy" },
  FRA: { dialCode: "+33", flag: "🇫🇷", name: "France" },

  // A
  AFG: { dialCode: "+93", flag: "🇦🇫", name: "Afghanistan" },
  ALB: { dialCode: "+355", flag: "🇦🇱", name: "Albania" },
  DZA: { dialCode: "+213", flag: "🇩🇿", name: "Algeria" },
  ASM: { dialCode: "+1684", flag: "🇦🇸", name: "American Samoa" },
  AND: { dialCode: "+376", flag: "🇦🇩", name: "Andorra" },
  AGO: { dialCode: "+244", flag: "🇦🇴", name: "Angola" },
  AIA: { dialCode: "+1264", flag: "🇦🇮", name: "Anguilla" },
  ATG: { dialCode: "+1268", flag: "🇦🇬", name: "Antigua and Barbuda" },
  ARG: { dialCode: "+54", flag: "🇦🇷", name: "Argentina" },
  ARM: { dialCode: "+374", flag: "🇦🇲", name: "Armenia" },
  ABW: { dialCode: "+297", flag: "🇦🇼", name: "Aruba" },
  AUS: { dialCode: "+61", flag: "🇦🇺", name: "Australia" },
  AZE: { dialCode: "+994", flag: "🇦🇿", name: "Azerbaijan" },

  // B
  BHS: { dialCode: "+1242", flag: "🇧🇸", name: "Bahamas" },
  BHR: { dialCode: "+973", flag: "🇧🇭", name: "Bahrain" },
  BGD: { dialCode: "+880", flag: "🇧🇩", name: "Bangladesh" },
  BRB: { dialCode: "+1246", flag: "🇧🇧", name: "Barbados" },
  BLR: { dialCode: "+375", flag: "🇧🇾", name: "Belarus" },
  BEL: { dialCode: "+32", flag: "🇧🇪", name: "Belgium" },
  BLZ: { dialCode: "+501", flag: "🇧🇿", name: "Belize" },
  BEN: { dialCode: "+229", flag: "🇧🇯", name: "Benin" },
  BMU: { dialCode: "+1441", flag: "🇧🇲", name: "Bermuda" },
  BTN: { dialCode: "+975", flag: "🇧🇹", name: "Bhutan" },
  BOL: { dialCode: "+591", flag: "🇧🇴", name: "Bolivia" },
  BIH: { dialCode: "+387", flag: "🇧🇦", name: "Bosnia and Herzegovina" },
  BWA: { dialCode: "+267", flag: "🇧🇼", name: "Botswana" },
  BRA: { dialCode: "+55", flag: "🇧🇷", name: "Brazil" },
  BRN: { dialCode: "+673", flag: "🇧🇳", name: "Brunei" },
  BGR: { dialCode: "+359", flag: "🇧🇬", name: "Bulgaria" },
  BFA: { dialCode: "+226", flag: "🇧🇫", name: "Burkina Faso" },
  BDI: { dialCode: "+257", flag: "🇧🇮", name: "Burundi" },

  // C
  CPV: { dialCode: "+238", flag: "🇨🇻", name: "Cabo Verde" },
  KHM: { dialCode: "+855", flag: "🇰🇭", name: "Cambodia" },
  CMR: { dialCode: "+237", flag: "🇨🇲", name: "Cameroon" },
  CAN: { dialCode: "+1", flag: "🇨🇦", name: "Canada" },
  CYM: { dialCode: "+1345", flag: "🇰🇾", name: "Cayman Islands" },
  CAF: { dialCode: "+236", flag: "🇨🇫", name: "Central African Republic" },
  TCD: { dialCode: "+235", flag: "🇹🇩", name: "Chad" },
  CHL: { dialCode: "+56", flag: "🇨🇱", name: "Chile" },
  CHN: { dialCode: "+86", flag: "🇨🇳", name: "China" },
  COL: { dialCode: "+57", flag: "🇨🇴", name: "Colombia" },
  COM: { dialCode: "+269", flag: "🇰🇲", name: "Comoros" },
  COG: { dialCode: "+242", flag: "🇨🇬", name: "Congo" },
  COD: { dialCode: "+243", flag: "🇨🇩", name: "Congo (DRC)" },
  COK: { dialCode: "+682", flag: "🇨🇰", name: "Cook Islands" },
  CRI: { dialCode: "+506", flag: "🇨🇷", name: "Costa Rica" },
  CIV: { dialCode: "+225", flag: "🇨🇮", name: "Côte d'Ivoire" },
  CUB: { dialCode: "+53", flag: "🇨🇺", name: "Cuba" },
  CUW: { dialCode: "+599", flag: "🇨🇼", name: "Curaçao" },
  CYP: { dialCode: "+357", flag: "🇨🇾", name: "Cyprus" },
  CZE: { dialCode: "+420", flag: "🇨🇿", name: "Czech Republic" },

  // D
  DNK: { dialCode: "+45", flag: "🇩🇰", name: "Denmark" },
  DJI: { dialCode: "+253", flag: "🇩🇯", name: "Djibouti" },
  DMA: { dialCode: "+1767", flag: "🇩🇲", name: "Dominica" },
  DOM: { dialCode: "+1809", flag: "🇩🇴", name: "Dominican Republic" },

  // E
  ECU: { dialCode: "+593", flag: "🇪🇨", name: "Ecuador" },
  EGY: { dialCode: "+20", flag: "🇪🇬", name: "Egypt" },
  SLV: { dialCode: "+503", flag: "🇸🇻", name: "El Salvador" },
  GNQ: { dialCode: "+240", flag: "🇬🇶", name: "Equatorial Guinea" },
  ERI: { dialCode: "+291", flag: "🇪🇷", name: "Eritrea" },
  EST: { dialCode: "+372", flag: "🇪🇪", name: "Estonia" },
  SWZ: { dialCode: "+268", flag: "🇸🇿", name: "Eswatini" },
  ETH: { dialCode: "+251", flag: "🇪🇹", name: "Ethiopia" },

  // F
  FLK: { dialCode: "+500", flag: "🇫🇰", name: "Falkland Islands" },
  FRO: { dialCode: "+298", flag: "🇫🇴", name: "Faroe Islands" },
  FJI: { dialCode: "+679", flag: "🇫🇯", name: "Fiji" },
  FIN: { dialCode: "+358", flag: "🇫🇮", name: "Finland" },
  GUF: { dialCode: "+594", flag: "🇬🇫", name: "French Guiana" },
  PYF: { dialCode: "+689", flag: "🇵🇫", name: "French Polynesia" },

  // G
  GAB: { dialCode: "+241", flag: "🇬🇦", name: "Gabon" },
  GMB: { dialCode: "+220", flag: "🇬🇲", name: "Gambia" },
  GEO: { dialCode: "+995", flag: "🇬🇪", name: "Georgia" },
  GHA: { dialCode: "+233", flag: "🇬🇭", name: "Ghana" },
  GIB: { dialCode: "+350", flag: "🇬🇮", name: "Gibraltar" },
  GRC: { dialCode: "+30", flag: "🇬🇷", name: "Greece" },
  GRL: { dialCode: "+299", flag: "🇬🇱", name: "Greenland" },
  GRD: { dialCode: "+1473", flag: "🇬🇩", name: "Grenada" },
  GLP: { dialCode: "+590", flag: "🇬🇵", name: "Guadeloupe" },
  GUM: { dialCode: "+1671", flag: "🇬🇺", name: "Guam" },
  GTM: { dialCode: "+502", flag: "🇬🇹", name: "Guatemala" },
  GGY: { dialCode: "+44", flag: "🇬🇬", name: "Guernsey" },
  GIN: { dialCode: "+224", flag: "🇬🇳", name: "Guinea" },
  GNB: { dialCode: "+245", flag: "🇬🇼", name: "Guinea-Bissau" },
  GUY: { dialCode: "+592", flag: "🇬🇾", name: "Guyana" },

  // H
  HTI: { dialCode: "+509", flag: "🇭🇹", name: "Haiti" },
  HND: { dialCode: "+504", flag: "🇭🇳", name: "Honduras" },
  HKG: { dialCode: "+852", flag: "🇭🇰", name: "Hong Kong" },
  HUN: { dialCode: "+36", flag: "🇭🇺", name: "Hungary" },

  // I
  ISL: { dialCode: "+354", flag: "🇮🇸", name: "Iceland" },
  IND: { dialCode: "+91", flag: "🇮🇳", name: "India" },
  IDN: { dialCode: "+62", flag: "🇮🇩", name: "Indonesia" },
  IRN: { dialCode: "+98", flag: "🇮🇷", name: "Iran" },
  IRQ: { dialCode: "+964", flag: "🇮🇶", name: "Iraq" },
  IRL: { dialCode: "+353", flag: "🇮🇪", name: "Ireland" },
  IMN: { dialCode: "+44", flag: "🇮🇲", name: "Isle of Man" },
  ISR: { dialCode: "+972", flag: "🇮🇱", name: "Israel" },

  // J
  JAM: { dialCode: "+1876", flag: "🇯🇲", name: "Jamaica" },
  JPN: { dialCode: "+81", flag: "🇯🇵", name: "Japan" },
  JEY: { dialCode: "+44", flag: "🇯🇪", name: "Jersey" },
  JOR: { dialCode: "+962", flag: "🇯🇴", name: "Jordan" },

  // K
  KAZ: { dialCode: "+7", flag: "🇰🇿", name: "Kazakhstan" },
  KEN: { dialCode: "+254", flag: "🇰🇪", name: "Kenya" },
  KIR: { dialCode: "+686", flag: "🇰🇮", name: "Kiribati" },
  PRK: { dialCode: "+850", flag: "🇰🇵", name: "North Korea" },
  KOR: { dialCode: "+82", flag: "🇰🇷", name: "South Korea" },
  KWT: { dialCode: "+965", flag: "🇰🇼", name: "Kuwait" },
  KGZ: { dialCode: "+996", flag: "🇰🇬", name: "Kyrgyzstan" },

  // L
  LAO: { dialCode: "+856", flag: "🇱🇦", name: "Laos" },
  LVA: { dialCode: "+371", flag: "🇱🇻", name: "Latvia" },
  LBN: { dialCode: "+961", flag: "🇱🇧", name: "Lebanon" },
  LSO: { dialCode: "+266", flag: "🇱🇸", name: "Lesotho" },
  LBR: { dialCode: "+231", flag: "🇱🇷", name: "Liberia" },
  LBY: { dialCode: "+218", flag: "🇱🇾", name: "Libya" },
  LIE: { dialCode: "+423", flag: "🇱🇮", name: "Liechtenstein" },
  LTU: { dialCode: "+370", flag: "🇱🇹", name: "Lithuania" },
  LUX: { dialCode: "+352", flag: "🇱🇺", name: "Luxembourg" },

  // M
  MAC: { dialCode: "+853", flag: "🇲🇴", name: "Macao" },
  MDG: { dialCode: "+261", flag: "🇲🇬", name: "Madagascar" },
  MWI: { dialCode: "+265", flag: "🇲🇼", name: "Malawi" },
  MYS: { dialCode: "+60", flag: "🇲🇾", name: "Malaysia" },
  MDV: { dialCode: "+960", flag: "🇲🇻", name: "Maldives" },
  MLI: { dialCode: "+223", flag: "🇲🇱", name: "Mali" },
  MLT: { dialCode: "+356", flag: "🇲🇹", name: "Malta" },
  MHL: { dialCode: "+692", flag: "🇲🇭", name: "Marshall Islands" },
  MTQ: { dialCode: "+596", flag: "🇲🇶", name: "Martinique" },
  MRT: { dialCode: "+222", flag: "🇲🇷", name: "Mauritania" },
  MUS: { dialCode: "+230", flag: "🇲🇺", name: "Mauritius" },
  MYT: { dialCode: "+262", flag: "🇾🇹", name: "Mayotte" },
  MEX: { dialCode: "+52", flag: "🇲🇽", name: "Mexico" },
  FSM: { dialCode: "+691", flag: "🇫🇲", name: "Micronesia" },
  MDA: { dialCode: "+373", flag: "🇲🇩", name: "Moldova" },
  MCO: { dialCode: "+377", flag: "🇲🇨", name: "Monaco" },
  MNG: { dialCode: "+976", flag: "🇲🇳", name: "Mongolia" },
  MNE: { dialCode: "+382", flag: "🇲🇪", name: "Montenegro" },
  MSR: { dialCode: "+1664", flag: "🇲🇸", name: "Montserrat" },
  MAR: { dialCode: "+212", flag: "🇲🇦", name: "Morocco" },
  MOZ: { dialCode: "+258", flag: "🇲🇿", name: "Mozambique" },
  MMR: { dialCode: "+95", flag: "🇲🇲", name: "Myanmar" },

  // N
  NAM: { dialCode: "+264", flag: "🇳🇦", name: "Namibia" },
  NRU: { dialCode: "+674", flag: "🇳🇷", name: "Nauru" },
  NPL: { dialCode: "+977", flag: "🇳🇵", name: "Nepal" },
  NLD: { dialCode: "+31", flag: "🇳🇱", name: "Netherlands" },
  NCL: { dialCode: "+687", flag: "🇳🇨", name: "New Caledonia" },
  NZL: { dialCode: "+64", flag: "🇳🇿", name: "New Zealand" },
  NIC: { dialCode: "+505", flag: "🇳🇮", name: "Nicaragua" },
  NER: { dialCode: "+227", flag: "🇳🇪", name: "Niger" },
  NGA: { dialCode: "+234", flag: "🇳🇬", name: "Nigeria" },
  NIU: { dialCode: "+683", flag: "🇳🇺", name: "Niue" },
  NFK: { dialCode: "+672", flag: "🇳🇫", name: "Norfolk Island" },
  MKD: { dialCode: "+389", flag: "🇲🇰", name: "North Macedonia" },
  MNP: { dialCode: "+1670", flag: "🇲🇵", name: "Northern Mariana Islands" },
  NOR: { dialCode: "+47", flag: "🇳🇴", name: "Norway" },

  // O
  OMN: { dialCode: "+968", flag: "🇴🇲", name: "Oman" },

  // P
  PAK: { dialCode: "+92", flag: "🇵🇰", name: "Pakistan" },
  PLW: { dialCode: "+680", flag: "🇵🇼", name: "Palau" },
  PSE: { dialCode: "+970", flag: "🇵🇸", name: "Palestine" },
  PAN: { dialCode: "+507", flag: "🇵🇦", name: "Panama" },
  PNG: { dialCode: "+675", flag: "🇵🇬", name: "Papua New Guinea" },
  PRY: { dialCode: "+595", flag: "🇵🇾", name: "Paraguay" },
  PER: { dialCode: "+51", flag: "🇵🇪", name: "Peru" },
  PHL: { dialCode: "+63", flag: "🇵🇭", name: "Philippines" },
  POL: { dialCode: "+48", flag: "🇵🇱", name: "Poland" },
  PRT: { dialCode: "+351", flag: "🇵🇹", name: "Portugal" },
  PRI: { dialCode: "+1787", flag: "🇵🇷", name: "Puerto Rico" },

  // Q
  QAT: { dialCode: "+974", flag: "🇶🇦", name: "Qatar" },

  // R
  REU: { dialCode: "+262", flag: "🇷🇪", name: "Réunion" },
  ROU: { dialCode: "+40", flag: "🇷🇴", name: "Romania" },
  RUS: { dialCode: "+7", flag: "🇷🇺", name: "Russia" },
  RWA: { dialCode: "+250", flag: "🇷🇼", name: "Rwanda" },

  // S
  BLM: { dialCode: "+590", flag: "🇧🇱", name: "Saint Barthélemy" },
  SHN: { dialCode: "+290", flag: "🇸🇭", name: "Saint Helena" },
  KNA: { dialCode: "+1869", flag: "🇰🇳", name: "Saint Kitts and Nevis" },
  LCA: { dialCode: "+1758", flag: "🇱🇨", name: "Saint Lucia" },
  MAF: { dialCode: "+590", flag: "🇲🇫", name: "Saint Martin" },
  SPM: { dialCode: "+508", flag: "🇵🇲", name: "Saint Pierre and Miquelon" },
  VCT: { dialCode: "+1784", flag: "🇻🇨", name: "Saint Vincent and the Grenadines" },
  WSM: { dialCode: "+685", flag: "🇼🇸", name: "Samoa" },
  SMR: { dialCode: "+378", flag: "🇸🇲", name: "San Marino" },
  STP: { dialCode: "+239", flag: "🇸🇹", name: "São Tomé and Príncipe" },
  SAU: { dialCode: "+966", flag: "🇸🇦", name: "Saudi Arabia" },
  SEN: { dialCode: "+221", flag: "🇸🇳", name: "Senegal" },
  SRB: { dialCode: "+381", flag: "🇷🇸", name: "Serbia" },
  SYC: { dialCode: "+248", flag: "🇸🇨", name: "Seychelles" },
  SLE: { dialCode: "+232", flag: "🇸🇱", name: "Sierra Leone" },
  SGP: { dialCode: "+65", flag: "🇸🇬", name: "Singapore" },
  SXM: { dialCode: "+1721", flag: "🇸🇽", name: "Sint Maarten" },
  SVK: { dialCode: "+421", flag: "🇸🇰", name: "Slovakia" },
  SLB: { dialCode: "+677", flag: "🇸🇧", name: "Solomon Islands" },
  SOM: { dialCode: "+252", flag: "🇸🇴", name: "Somalia" },
  ZAF: { dialCode: "+27", flag: "🇿🇦", name: "South Africa" },
  SSD: { dialCode: "+211", flag: "🇸🇸", name: "South Sudan" },
  ESP: { dialCode: "+34", flag: "🇪🇸", name: "Spain" },
  LKA: { dialCode: "+94", flag: "🇱🇰", name: "Sri Lanka" },
  SDN: { dialCode: "+249", flag: "🇸🇩", name: "Sudan" },
  SUR: { dialCode: "+597", flag: "🇸🇷", name: "Suriname" },
  SWE: { dialCode: "+46", flag: "🇸🇪", name: "Sweden" },
  CHE: { dialCode: "+41", flag: "🇨🇭", name: "Switzerland" },
  SYR: { dialCode: "+963", flag: "🇸🇾", name: "Syria" },

  // T
  TWN: { dialCode: "+886", flag: "🇹🇼", name: "Taiwan" },
  TJK: { dialCode: "+992", flag: "🇹🇯", name: "Tajikistan" },
  TZA: { dialCode: "+255", flag: "🇹🇿", name: "Tanzania" },
  THA: { dialCode: "+66", flag: "🇹🇭", name: "Thailand" },
  TLS: { dialCode: "+670", flag: "🇹🇱", name: "Timor-Leste" },
  TGO: { dialCode: "+228", flag: "🇹🇬", name: "Togo" },
  TKL: { dialCode: "+690", flag: "🇹🇰", name: "Tokelau" },
  TON: { dialCode: "+676", flag: "🇹🇴", name: "Tonga" },
  TTO: { dialCode: "+1868", flag: "🇹🇹", name: "Trinidad and Tobago" },
  TUN: { dialCode: "+216", flag: "🇹🇳", name: "Tunisia" },
  TUR: { dialCode: "+90", flag: "🇹🇷", name: "Turkey" },
  TKM: { dialCode: "+993", flag: "🇹🇲", name: "Turkmenistan" },
  TCA: { dialCode: "+1649", flag: "🇹🇨", name: "Turks and Caicos Islands" },
  TUV: { dialCode: "+688", flag: "🇹🇻", name: "Tuvalu" },

  // U
  UGA: { dialCode: "+256", flag: "🇺🇬", name: "Uganda" },
  UKR: { dialCode: "+380", flag: "🇺🇦", name: "Ukraine" },
  ARE: { dialCode: "+971", flag: "🇦🇪", name: "United Arab Emirates" },
  GBR: { dialCode: "+44", flag: "🇬🇧", name: "United Kingdom" },
  USA: { dialCode: "+1", flag: "🇺🇸", name: "United States" },
  URY: { dialCode: "+598", flag: "🇺🇾", name: "Uruguay" },
  UZB: { dialCode: "+998", flag: "🇺🇿", name: "Uzbekistan" },

  // V
  VUT: { dialCode: "+678", flag: "🇻🇺", name: "Vanuatu" },
  VAT: { dialCode: "+379", flag: "🇻🇦", name: "Vatican City" },
  VEN: { dialCode: "+58", flag: "🇻🇪", name: "Venezuela" },
  VNM: { dialCode: "+84", flag: "🇻🇳", name: "Vietnam" },
  VGB: { dialCode: "+1284", flag: "🇻🇬", name: "British Virgin Islands" },
  VIR: { dialCode: "+1340", flag: "🇻🇮", name: "U.S. Virgin Islands" },

  // W
  WLF: { dialCode: "+681", flag: "🇼🇫", name: "Wallis and Futuna" },

  // Y
  YEM: { dialCode: "+967", flag: "🇾🇪", name: "Yemen" },

  // Z
  ZMB: { dialCode: "+260", flag: "🇿🇲", name: "Zambia" },
  ZWE: { dialCode: "+263", flag: "🇿🇼", name: "Zimbabwe" },
};

/** ISO3 codes shown above the divider in the prefix dropdown. */
export const DIAL_CODE_PRIORITY: string[] = ["HRV", "SVN", "DEU", "AUT", "ITA", "FRA"];
